import { ChevronDown } from "@acto/web-common/components";
import { styled } from "@pigment-css/react";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import type { ManagementSearchAndFilterBarProps } from "./types";
import { ManagementSearchInput } from "../primitives/SearchInput";

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: `calc(100% + 2 * ${theme.managementView.spacing[16]})`,
  marginInline: `-${theme.managementView.spacing[16]}`,
  gap: theme.managementView.spacing[16],
}));

const SearchContainer = styled("div")({
  flex: "0 0 275px",
});

const FiltersContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[16],
}));

const FilterDropdownButton = styled("button")(({ theme }) => ({
  height: "50px",
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  padding: `0 ${theme.managementView.spacing[24]}`,
  backgroundColor: "transparent",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: "50px",
  cursor: "pointer",
  transition: "background-color 0.2s",
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[56],

  "& svg": {
    height: "12px",
    width: "12px",
  },

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

const DropdownContent = styled(RadixDropdown.Content)(({ theme }) => ({
  minWidth: "160px",
  backgroundColor: theme.managementView.colors.neutral[0],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: theme.managementView.radius[16],
  boxShadow: theme.shadows[2],
  padding: theme.managementView.spacing[8],
  zIndex: 10000,
  marginTop: theme.managementView.spacing[4],
}));

const DropdownMenuItem = styled(RadixDropdown.Item)(({ theme }) => ({
  color: theme.managementView.colors.neutral[100],
  padding: `${theme.managementView.spacing[8]} ${theme.managementView.spacing[18]}`,
  borderRadius: theme.managementView.radius[16],
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  fontFamily: theme.managementView.font.family,
  backgroundColor: "transparent",
  border: "none",
  outline: "none",
  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
  "&:focus": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

// TODO: The filters are not defined yet -> hence we only have one basic inline default, which is
// a simple dropdown with a single selection. This needs to be extracted into its own component once
// filters are finalised and more complex logic is needed.
export const ManagementSearchAndFilterBar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filterDropdowns,
  additionalFilters,
  className,
}: ManagementSearchAndFilterBarProps) => {
  return (
    <Container className={className}>
      <SearchContainer>
        <ManagementSearchInput
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          placeholder={searchPlaceholder}
        />
      </SearchContainer>

      <FiltersContainer>
        {filterDropdowns.map((dropdown) => {
          const selectedOption = dropdown.options.find((opt) => opt.value === dropdown.selectedValue);
          const displayLabel = selectedOption ? selectedOption.label : dropdown.label;

          return (
            <RadixDropdown.Root key={dropdown.key}>
              <RadixDropdown.Trigger asChild>
                <FilterDropdownButton>
                  {displayLabel}
                  <ChevronDown />
                </FilterDropdownButton>
              </RadixDropdown.Trigger>
              <RadixDropdown.Portal>
                <DropdownContent align="start">
                  {dropdown.options.map((option) => (
                    <DropdownMenuItem key={option.value} onSelect={() => dropdown.onSelect(option.value)}>
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownContent>
              </RadixDropdown.Portal>
            </RadixDropdown.Root>
          );
        })}
        {additionalFilters}
      </FiltersContainer>
    </Container>
  );
};
